import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient'; 
import { 
  ChevronLeft, ChevronRight, X, CheckCircle2,ChevronDown
} from 'lucide-react';

const Hero = () => {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingStep, setBookingStep] = useState('form');
  const [name, setName] = useState('');

  const categories = ["Luxury Cars", "Premium Cars", "Economy Cars", "EVs", "Luxury Van", "Mini Coach", "Large Bus"];

  useEffect(() => {
    const getHeroData = async () => {
      try {
        const { data, error } = await supabase
          .from('hero_slides')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) throw error;
        if (data) setSlides(data);
      } catch (error) {
        console.error('Error:', error.message);
      } finally {
        setLoading(false);
      }
    };

    getHeroData();
  }, []);

  const nextSlide = useCallback(() => {
    if (slides.length === 0) return;
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const prevSlide = () => {
    if (slides.length === 0) return;
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  useEffect(() => {
    if (slides.length > 0) {
      const timer = setInterval(nextSlide, 10000);
      return () => clearInterval(timer);
    }
  }, [nextSlide, slides.length]);

  if (loading) return <div className="h-[60vh] md:h-[95vh] bg-black flex items-center justify-center text-white font-black tracking-widest">LOADING...</div>;

  return (
    <section className="relative h-[60vh] md:h-[95vh] w-full overflow-hidden group bg-black font-sans">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-all duration-1000 ease-in-out transform ${
            index === currentSlide ? 'opacity-100 scale-100 z-20' : 'opacity-0 scale-105 z-10'
          }`}
        >
          {/* Overlay - Mobile par thoda dark rakha hai text visibility ke liye */}
          <div className="absolute inset-0 bg-black/50 md:bg-black/30 z-10" />
          
          <img 
            src={slide.image_url} 
            alt={slide.title} 
            className="w-full h-full object-cover object-center md:object-top" 
          />
          
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-white px-6">
            <div className="flex items-center gap-2 mb-4 animate-in fade-in slide-in-from-bottom-2 duration-700">
              <span className="h-[1px] w-6 md:w-10 bg-blue-500"></span>
              <p className="text-white font-bold tracking-[0.2em] text-[9px] md:text-xs uppercase">
                Global prominence | Since 1999
              </p>
              <span className="h-[1px] w-6 md:w-10 bg-blue-500"></span>
            </div>
            
            <h1 className="text-2xl md:text-5xl lg:text-7xl font-black mb-4 text-center tracking-tighter uppercase italic leading-[1.1] max-w-[95%] md:max-w-[80%] drop-shadow-2xl">
              {slide.title}
            </h1>
            
            <p className="text-[12px] md:text-lg mb-8 text-center text-gray-200 font-medium max-w-[300px] md:max-w-xl leading-relaxed drop-shadow-lg">
              {slide.subtitle}
            </p>
            
            <div className="flex flex-row gap-4"> 
              <button onClick={() => setShowBookingModal(true)} className="px-6 py-3.5 md:px-10 md:py-5 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase text-[10px] tracking-widest transition-all shadow-xl active:scale-95">
                Book Now
              </button>
              <button onClick={() => navigate('/luxury-car')} className="px-6 py-3.5 md:px-10 md:py-5 bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white hover:text-black text-white font-black uppercase text-[10px] tracking-widest transition-all active:scale-95">
                Our Fleet
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Responsive Navigation Arrows */}
      <button 
        onClick={prevSlide} 
        className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-40 p-2 md:p-4 bg-black/20 md:bg-black/40 text-white backdrop-blur-sm border border-white/10 hover:bg-blue-600 transition-all rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100"
      >
        <ChevronLeft size={20} className="md:w-7 md:h-7" />
      </button>
      <button 
        onClick={nextSlide} 
        className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-40 p-2 md:p-4 bg-black/20 md:bg-black/40 text-white backdrop-blur-sm border border-white/10 hover:bg-blue-600 transition-all rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100"
      >
        <ChevronRight size={20} className="md:w-7 md:h-7" />
      </button>

      {/* Progress Bar Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 flex gap-2">
        {slides.map((_, i) => (
          <div 
            key={i} 
            className={`h-1 transition-all duration-500 ${i === currentSlide ? 'w-8 bg-blue-500' : 'w-2 bg-white/30'}`}
          />
        ))}
      </div>

      {/* Booking Modal Logic is same but added 'animate-in' for smoothness */}
      {showBookingModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-[#0a1128]/95 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setShowBookingModal(false)}></div>
          <div className="relative bg-white w-full max-w-[380px] rounded-none shadow-2xl border border-white/10 animate-in zoom-in-95 duration-300">
            {/* Modal Content stays as your previous design but more stable */}
            <div className="bg-[#0a1128] py-4 px-6 flex justify-between items-center">
              <h3 className="text-white text-[10px] font-black uppercase tracking-[0.2em]">Request Quote</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-white/60 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 md:p-8">
              {bookingStep === 'form' ? (
                <>
                <iframe title="Booking Form Submission" name="hidden_iframe_fleet" style={{ display: "none" }}></iframe>
                    <form
                      action="https://formsubmit.co/enquiry@expresstravelcorp.com"
                      method="POST"
                      target="hidden_iframe_fleet"
                      onSubmit={() => {
                        setTimeout(() => setBookingStep('success'), 1000);
                      }}
                     className="space-y-3"
                    >
                      {/* FormSubmit Config */}
                <input type="hidden" name="_subject" value={`New Enquiry from ${name}`} />
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_template" value="table" />
                <input type="text" name="_honey" style={{ display: "none" }} />
                   <div className="space-y-0.5">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Full Name</label>
                     <input required type="text" placeholder="YOUR NAME" name='name' className="w-full p-2.5 bg-gray-50 border border-slate-200 outline-none focus:border-blue-600 text-[10px] font-bold uppercase rounded-none" 
                     onChange={(e)=>{setName(e.target.value)}} />
                   </div>
                   <div className="space-y-0.5">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact Number</label>
                     <input required type="tel" placeholder="+91" name='phone' className="w-full p-2.5 bg-gray-50 border border-slate-200 outline-none focus:border-blue-600 text-[10px] font-bold rounded-none" />
                   </div>
                   <div className="space-y-0.5">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Email Address</label>
                     <input required type="email" placeholder="EMAIL@COMPANY.COM" name='email' className="w-full p-2.5 bg-gray-50 border border-slate-200 outline-none focus:border-blue-600 text-[10px] font-bold uppercase rounded-none" />
                   </div>
                   <div className="space-y-0.5 text-left relative">
                     <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Vehicle Category</label>
                     <div className="relative">
                       <select required name='service' className="w-full p-2.5 bg-gray-50 border border-slate-200 outline-none focus:border-blue-600 text-[10px] font-black uppercase rounded-none cursor-pointer appearance-none pr-10">
                          <option value="" disabled selected>Select Category</option>
                          {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                       </select>
                       <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                     </div>
                   </div>
                   <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-0.5">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Start Date</label>
                          <input required type="date" max="9999-12-31" name='startDate' className="w-full p-2 bg-gray-50 border border-slate-200 outline-none focus:border-blue-600 text-[9px] font-bold rounded-none" />
                        </div>
                        <div className="space-y-0.5">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">End Date</label>
                          <input required type="date" max="9999-12-31" name='endDate' className="w-full p-2 bg-gray-50 border border-slate-200 outline-none focus:border-blue-600 text-[9px] font-bold rounded-none" />
                        </div>
                   </div>
                   <button type="submit" className="w-full bg-blue-600 hover:bg-[#0a1128] text-white py-3.5 mt-2 font-black text-[10px] uppercase tracking-[0.2em] transition-all rounded-none shadow-lg active:scale-95">
                     Confirm Request
                   </button>
                </form>
                </>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle2 size={48} className="text-green-500 mx-auto mb-4" />
                  <h4 className="font-black uppercase text-sm mb-2">Request Received</h4>
                  <button onClick={() => { setShowBookingModal(false); setBookingStep('form'); }} className="w-full border-2 border-[#0a1128] py-3 font-black text-[10px] uppercase mt-4 hover:bg-[#0a1128] hover:text-white transition-all">Close</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;